import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../core/http/api.service';
import { UtilsService } from '../../core/services/utils/utils.service';

@Component({
  selector: 'app-user-funding-records',
  templateUrl: './user-funding-records.component.html',
  styleUrls: ['./user-funding-records.component.css'],
  standalone: false,
})
export class UserFundingRecordsComponent implements OnInit {
  recordsPayload: any = { records: [], total: 0, page: 1, per_page: 10 };
  loading = true;
  loaders = Array(8).fill(0);
  page = 1;
  perPage = 10;
  verificationUpdatingById: Record<string, boolean> = {};
  filters = {
    keyword: '',
    verificationStatus: 'all',
    sortBy: 'created_desc',
  };

  constructor(
    private api: ApiService,
    private utils: UtilsService,
  ) {}

  ngOnInit(): void {
    this.getFundingRecords();
  }

  buildQuery(page: number = 1): any {
    const query: any = {
      page,
      per_page: this.perPage,
      sort_by: this.filters.sortBy,
    };
    const keyword = String(this.filters.keyword || '').trim();
    if (keyword) query.keyword = keyword;
    if (this.filters.verificationStatus !== 'all') {
      query.verification_status = this.filters.verificationStatus;
    }
    return query;
  }

  getFundingRecords(newPage: number = this.page): void {
    this.page = newPage;
    this.loading = true;
    this.api.getAdminFundingRecords(this.buildQuery(newPage)).subscribe(
      (res) => {
        this.recordsPayload = res?.response || {
          records: [],
          total: 0,
          page: newPage,
          per_page: this.perPage,
        };
        this.loading = false;
      },
      (err) => {
        this.utils.resErr(err);
        this.loading = false;
      },
    );
  }

  pageChange(event: number): void {
    this.getFundingRecords(event);
  }

  applyFilters(): void {
    this.getFundingRecords(1);
  }

  clearFilters(): void {
    this.filters = {
      keyword: '',
      verificationStatus: 'all',
      sortBy: 'created_desc',
    };
    this.getFundingRecords(1);
  }

  hasActiveFilters(): boolean {
    return (
      String(this.filters.keyword || '').trim().length > 0 ||
      this.filters.verificationStatus !== 'all' ||
      this.filters.sortBy !== 'created_desc'
    );
  }

  handleFilterInputKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') this.applyFilters();
  }

  getAttachments(record: any = {}): any[] {
    const attachments = Array.isArray(record?.adminPrivateAttachments)
      ? record.adminPrivateAttachments
      : [];
    if (attachments.length) return attachments;
    return record?.adminPrivateAttachment
      ? [record.adminPrivateAttachment]
      : [];
  }

  getAttachmentIconClass(attachment: any = {}): string {
    const filename = String(attachment?.filename || '').toLowerCase();
    const mimetype = String(attachment?.mimetype || '').toLowerCase();
    const extension = filename.match(/\.([a-z0-9]+)$/)?.[1] || '';
    if (
      ['jpg', 'jpeg', 'png', 'webp'].includes(extension) ||
      mimetype.startsWith('image/')
    ) {
      return 'las la-file-image attachment-icon is-image';
    }
    if (extension === 'pdf' || mimetype === 'application/pdf') {
      return 'las la-file-pdf attachment-icon is-pdf';
    }
    if (
      ['xlsx', 'xls'].includes(extension) ||
      mimetype.includes('spreadsheetml.sheet') ||
      mimetype === 'application/vnd.ms-excel'
    ) {
      return 'las la-file-excel attachment-icon is-sheet';
    }
    if (['docx', 'doc'].includes(extension) || mimetype.includes('word')) {
      return 'las la-file-word attachment-icon is-doc';
    }
    return 'las la-file-alt attachment-icon is-file';
  }

  viewDocument(attachment: any = {}): void {
    const key = String(attachment?.key || '').trim();
    if (!key) {
      this.utils.toastError(
        'Document URL is not available. Please refresh and try again.',
      );
      return;
    }
    const viewerWindow = window.open('about:blank', '_blank');
    if (viewerWindow) {
      viewerWindow.opener = null;
      viewerWindow.document.write(
        '<!doctype html><title>Opening document...</title><body style="margin:0;display:grid;place-items:center;height:100vh;background:#111;color:#fff;font-family:Arial,sans-serif;">Opening document...</body>',
      );
      viewerWindow.document.close();
    }
    this.api.getFundingAttachmentAccessUrl(key).subscribe(
      (res) => {
        const url = String(res?.response?.url || res?.url || '').trim();
        if (!url) {
          viewerWindow?.close();
          this.utils.toastError(
            'Document URL is not available. Please refresh and try again.',
          );
          return;
        }
        if (viewerWindow) {
          viewerWindow.location.href = url;
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      },
      (err) => {
        viewerWindow?.close();
        this.utils.resErr(err);
      },
    );
  }

  getAttachmentUpdateKey(
    record: any = {},
    attachment: any = {},
    attachmentIndex: number = -1,
  ): string {
    const recordId = String(record?.id || '').trim();
    const attachmentKey = String(attachment?.key || attachmentIndex).trim();
    return `${recordId}:${attachmentKey}`;
  }

  updateVerification(
    record: any = {},
    verificationStatus: 'valid' | 'invalid',
    attachment: any = {},
    attachmentIndex: number = -1,
  ): void {
    const recordId = String(record?.id || '').trim();
    const updateKey = this.getAttachmentUpdateKey(
      record,
      attachment,
      attachmentIndex,
    );
    if (!recordId || this.verificationUpdatingById[updateKey]) return;

    this.verificationUpdatingById[updateKey] = true;
    this.api
      .updateAdminFundingRecordVerification(recordId, {
        verificationStatus,
        attachmentKey: String(attachment?.key || '').trim(),
        attachmentIndex,
      })
      .subscribe(
        () => {
          this.utils.toastSuccess('Verification status updated.');
          this.getFundingRecords(this.page);
          this.verificationUpdatingById[updateKey] = false;
        },
        (err) => {
          this.utils.resErr(err);
          this.verificationUpdatingById[updateKey] = false;
        },
      );
  }

  formatAmount(amount: any = 0, currency: string = 'USD'): string {
    const value = Number(amount || 0);
    const normalizedCurrency = String(currency || 'USD').toUpperCase();
    const locale = normalizedCurrency === 'INR' ? 'en-IN' : 'en-US';
    return `${normalizedCurrency} ${Number.isFinite(value) ? value.toLocaleString(locale) : '0'}`;
  }

  getVerificationLabel(status: string = ''): string {
    const normalized = String(status || 'pending').toLowerCase();
    if (normalized === 'valid') return 'Valid';
    if (normalized === 'invalid') return 'Invalid';
    return 'Pending';
  }
}
